import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useStopwatch } from 'react-timer-hook';
import TagManager from 'react-gtm-module';

import { LoadingType, PreQualificationStep, setLoadingState, setPreQualificationStep } from 'actions';
import { setData } from 'actions/data';
import { setPrequalificationPortal } from 'actions/prequalificationPortal';
import { RunApplicationUnderwritingDto, ApplicationUnderwritingDto, underwritingApi } from 'api/UnderwritingApi';
import { Button } from 'components/Buttons/Button';
import ContractorForm, { ContractorInputLabelsType } from 'pages/PreQualification/steps/ContractorInfo/ContractorForm';
import { FormDataContractorInfo } from 'pages/PreQualification/steps/ContractorInfo/ContractorForm/ContractorForm';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Loader from 'components/Loader';
import { ApplicationType } from 'enums/ApplicationType';
import { ButtonType } from 'enums/ButtonType';
import { ContractorInputName } from 'enums/ContractorInputName';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { UnderwritingResult } from 'enums/UnderwritingResult';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import { AppRoutes } from 'routes/routes.enums';
import { prepareBorrowerForUnderwriting, prepareCoBorrowerForUnderwriting } from 'utils/prepareBorrowerForUnderwriting';
import { getStreetAddress, removeNonDigits } from 'utils/stringUtils';
import { ContractorNameSource, PrequalificationVersion } from 'enums/ApplicationsVariables';
import { GOOGLE_TAG_MANAGER_ARGS } from 'constants/constants';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { getConceptualDeviceId, removeUrlMarketingParamsFromCookie } from 'routes/routes.utils';
import { marketingApi } from 'api/MarketingApi';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { IntermediaryRefer } from 'enums/IntermediaryRefer';

import styles from './ContractorInfo.module.scss';

const PLEASE_ENTER_AT_LEAST_FOUR_CHARACTERS = 'Please Enter at Least 4 Characters';

const ContractorInputLabel = {
  contractorName: 'Contractor Name',
  contractorNameRestrict: `Contractor Name - ${PLEASE_ENTER_AT_LEAST_FOUR_CHARACTERS}`,
  salespersonName: 'Name of the Salesperson of Contractor’s Company',
};

const enum ContractorInfoIframeMessageType {
  LoanInformation = 'LoanInformation',
  UnderwritingInformation = 'UnderwritingInformation',
}

const ContractorInfo = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const borrowerState = useAppSelector((state) => state.borrower);
  const coBorrowerState = useAppSelector((state) => state.coBorrower);
  const { application, borrower, coBorrower } = useAppSelector((store) => store);
  const {
    contractorName,
    salesPerson,
    salespersonData,
    utmSource,
    utmMedium,
    utmCampaign,
    campaignId,
    adGroupId,
    network,
    device,
    gclid,
    fbclid,
    liFatId,
    pintrestEpik,
    uetMsclkId,
    utmTerm,
    utmContent,
    promoCode,
    refer,
    contractorAutofillUsed,
    howDidYouHearAboutUs,
    howDidYouHearAboutUsComment,
    contractorId,
    jobTreadContractorName,
    jobTreadContractorUuid,
    jobTreadLoanAmount,
    jobTreadDocumentId,
  } = useAppSelector((state) => state.data);
  const { borrower: borrowerIncome, coBorrower: coBorrowerIncome } = useAppSelector((state) => state.income);
  const loading = useAppSelector((state) => state.loading[LoadingType.RunUnderwriting]);
  const { statuses, descriptions } = useAppSelector((state) => state.proveErrorInfo);
  const { isContractorNameInputDisabled, isContractorInfoCheckboxChecked } = useAppSelector(
    (state) => state.prequalificationPortal,
  );
  const { seconds, start } = useStopwatch({
    autoStart: false,
  });

  const [displayedMessage, setDisplayedMessage] = useState('HFS Is Reviewing Your Loan Inquiry');

  useEffect(() => {
    switch (seconds) {
      case 5:
        setDisplayedMessage('Just Another Moment');
        break;
      case 9:
        setDisplayedMessage('Almost There');
        break;
    }
  }, [seconds]);

  useEffect(() => {
    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.ContractorInformation,
          prequalificationVersion: PrequalificationVersion.First,
          borrowerId: borrower.borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const { watch, handleSubmit, control, formState, reset, setError, setValue, trigger, clearErrors } =
    useForm<FormDataContractorInfo>({
      defaultValues: {
        [ContractorInputName.ContractorName]: contractorName,
        [ContractorInputName.SalespersonName]: salesPerson,
        [ContractorInputName.AgreeToDocuments]: isContractorInfoCheckboxChecked,
        [ContractorInputName.ContractorAutofillUsed]: contractorName
          ? contractorAutofillUsed || ContractorNameSource.Empty
          : ContractorNameSource.Empty,
        contractorId: contractorId,
      },
    });

  const watcher = watch();

  const customReset = (contractorValues?: ContractorInputLabelsType) => {
    reset({
      [ContractorInputName.ContractorName]: contractorValues?.contractorName || '',
      [ContractorInputName.SalespersonName]: contractorValues?.salespersonName || '',
      [ContractorInputName.AgreeToDocuments]: watcher[ContractorInputName.AgreeToDocuments],
      [ContractorInputName.ContractorAutofillUsed]:
        contractorValues?.contractorAutofillUsed || ContractorNameSource.Empty,
      contractorId: contractorValues?.contractorId || '',
    });
  };

  const sendLoanInformationForIframeParentPage = () => {
    const isJoint = application[LoanInformationVariables.LoanType] === ApplicationType.Joint;

    const message = {
      type: ContractorInfoIframeMessageType.LoanInformation,
      data: JSON.stringify({
        loanAmount: application[LoanInformationVariables.LoanAmount],
        borrower: {
          firstName: borrowerState.firstName,
          lastName: borrowerState.lastName,
          email: borrowerState.email,
          phoneNumber: borrowerState.phone,
          street: getStreetAddress(borrowerState.streetNumber, borrowerState.streetName),
          city: borrowerState.city,
          state: borrowerState.state,
          zip: borrowerState.zip,
          county: borrowerState.county,
        },
        coborrower: isJoint
          ? {
              firstName: coBorrowerState.firstName,
              lastName: coBorrowerState.lastName,
              email: coBorrowerState.email,
              phoneNumber: coBorrowerState.phone,
            }
          : null,
      }),
    };

    window.parent.postMessage(message, '*');
  };

  const sendUnderwritingResultForIframeParentPage = (underwritingResponse: ApplicationUnderwritingDto) => {
    const message = {
      type: ContractorInfoIframeMessageType.UnderwritingInformation,
      data: JSON.stringify({
        result: underwritingResponse.result,
        applicationId: underwritingResponse.applicationId,
        applicationWasPrequalified: underwritingResponse.applicationWasPrequalified,
        borrowerId: underwritingResponse.borrowerId,
        coborrowerId: underwritingResponse.coborrowerId,
      }),
    };

    window.parent.postMessage(message, '*');
  };

  const onSubmit = handleSubmit(async (data) => {
    dispatch(
      setData({
        contractorName: data[ContractorInputName.ContractorName] ?? contractorName,
        salesPerson: data[ContractorInputName.SalespersonName] ?? salesPerson,
        contractorAutofillUsed: data[ContractorInputName.ContractorAutofillUsed],
      }),
    );

    dispatch(setLoadingState({ type: LoadingType.RunUnderwriting, loading: true }));
    start();

    try {
      sendLoanInformationForIframeParentPage();

      const isJoint = application[LoanInformationVariables.LoanType] === ApplicationType.Joint;
      const underwritingResponse = await underwritingApi.runUnderwriting({
        borrowerData: prepareBorrowerForUnderwriting(borrower),
        coborrowerData: isJoint ? prepareCoBorrowerForUnderwriting(coBorrower) : null,
        generalData: {
          loanAmount: +removeNonDigits(application![LoanInformationVariables.LoanAmount]),
          loanPurpose: application[LoanInformationVariables.LoanPurpose],
          otherHomeImprovementType: application![LoanInformationVariables.OtherHomeImprovementType],
          swimmingPoolType: application![LoanInformationVariables.SwimmingPoolType],
          applicationType: application![LoanInformationVariables.LoanType] as ApplicationType,
          contractorName: data[ContractorInputName.ContractorName] ?? contractorName,
          contractorId: contractorId || data.contractorId,
          contractorAutofillUsed: data[ContractorInputName.ContractorAutofillUsed],
          // Derived from the contractorAutofillUsed so the two always agree
          contractorPromoLinkUsed: data[ContractorInputName.ContractorAutofillUsed] === ContractorNameSource.PromoLink,
          salesPerson: data[ContractorInputName.SalespersonName] ?? salesPerson,
          IsBorrowerManualFlow: application?.IsBorrowerManualFlow,
          IsCoBorrowerManualFlow: application?.IsCoBorrowerManualFlow,
          BorrowerChangedInfoAfterProvePreFill: application?.BorrowerChangedInfoAfterProvePreFill,
          CoBorrowerChangedInfoAfterProvePreFill: application?.CoBorrowerChangedInfoAfterProvePreFill,
          utmSource: utmSource,
          utmMedium: utmMedium,
          utmCampaign: utmCampaign,
          campaignId: campaignId,
          adGroupId: adGroupId,
          network: network,
          device: device,
          gclid: gclid,
          fbclid: fbclid,
          liFatId: liFatId,
          pintrestEpik: pintrestEpik,
          uetMsclkId: uetMsclkId,
          utmTerm: utmTerm,
          utmContent: utmContent,
          promoCode: promoCode,
          refer: refer,
          howDidYouHearAboutUs: howDidYouHearAboutUs,
          howDidYouHearAboutUsComment: howDidYouHearAboutUsComment,
          proveErrorStatuses: statuses,
          proveErrorDescriptions: descriptions,
        },
        incomeData: {
          borrower: borrowerIncome,
          coBorrower: isJoint ? coBorrowerIncome : null,
        },
        ...(refer?.toLowerCase() === IntermediaryRefer.JobTread && {
          jobTreadData: {
            contractorUuid: jobTreadContractorUuid,
            documentId: jobTreadDocumentId,
            contractorName: jobTreadContractorName,
            loanAmount: jobTreadLoanAmount,
          },
        }),
      } as RunApplicationUnderwritingDto);

      dispatch(
        setData({
          contractorName: '',
          contractorId: '',
          salesPerson: '',
          salespersonData: {},
          promoCode: '',
          contractorAutofillUsed: ContractorNameSource.Empty,
        }),
      );
      dispatch(setPrequalificationPortal({ isContractorNameInputDisabled: false }));

      sendUnderwritingResultForIframeParentPage(underwritingResponse);

      switch (underwritingResponse?.result) {
        case UnderwritingResult.Decline: {
          TagManager.dataLayer(GOOGLE_TAG_MANAGER_ARGS.applicationDecline);
          dispatch(setPreQualificationStep(PreQualificationStep.NoOptions));
          break;
        }
        case UnderwritingResult.FrozenReport: {
          dispatch(setPreQualificationStep(PreQualificationStep.UnfreezeCredit));
          break;
        }
        case UnderwritingResult.Success: {
          TagManager.dataLayer(GOOGLE_TAG_MANAGER_ARGS.applicationPreApproved);
          removeUrlMarketingParamsFromCookie();
          navigator(AppRoutes.Dashboard);
          break;
        }
        default: {
          dispatch(setPreQualificationStep(PreQualificationStep.NotFoundContact));
        }
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(setLoadingState({ type: LoadingType.RunUnderwriting, loading: false }));
    }
  });

  const goPreviousStep = () => {
    if (application[LoanInformationVariables.LoanType] === ApplicationType.Individual) {
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
    } else {
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerIncome));
    }
  };

  return (
    <>
      {loading ? (
        <Loader message={displayedMessage} />
      ) : (
        <>
          <FormHeader title="Contractor Information" percent={88} />
          <FormBody onSubmit={onSubmit}>
            <ContractorForm
              watcher={watcher}
              setError={setError}
              errors={formState.errors}
              reset={customReset}
              control={control}
              labels={ContractorInputLabel}
              isDisabled={isContractorNameInputDisabled}
              setValue={setValue}
              trigger={trigger}
              clearErrors={clearErrors}
            />
          </FormBody>
          <div className={styles.buttonControlContainer}>
            <Button buttonType={ButtonType.Secondary} withArrow onClick={() => goPreviousStep()}>
              Back
            </Button>
            <Button type="submit" form="form">
              Submit
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default ContractorInfo;
